import { z } from 'zod';
import { AuditMeta, DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Wijzigingsverzoek (change request) — replaces the "email Linda" workflow.
 *
 * Firestore path: change_requests/{reqId}
 * Kept at root (not tenant-subcollection) because suppohrt_admin reviews
 * cross-tenant requests in one queue.
 */
export const ChangeRequestType = z.enum([
    'kennisbank_aanvulling',
    'sociale_kaart_update',
    'fout_melden',
    'antwoord_feedback',
    'overig',
]);
/**
 * Answer-quality label for `antwoord_feedback` requests — the "why" behind
 * a Kennisagent (Mila) thumbs-down. Each label routes to a different
 * improvement lever, so the review queue doubles as the eval dataset that
 * drives corpus / retrieval / prompt tuning. Curated enum by design: adding
 * a category is a code change (CLAUDE.md "curated enums over free-form").
 */
export const AnswerFeedbackLabel = z.enum([
    'feitelijk_onjuist', // clinically/factually wrong → fix source or prompt
    'bron_probleem', // wrong/missing/hallucinated citation → citation_validator
    'onvolledig', // partial, missing nuance/contra-indication → retrieval/prompt
    'verkeerde_toon', // register/length/"u-je" off → prompts.py
    'kennis_ontbreekt', // the answer isn't in the corpus → add a source (the gold signal)
]);
export const ChangeRequestStatus = z.enum([
    'new',
    'in_progress',
    'info_requested',
    'approved',
    'rejected',
]);
export const ChangeRequestSchema = AuditMeta.extend({
    id: DocId,
    tenantId: TenantId,
    submittedByUserId: DocId,
    type: ChangeRequestType,
    title: z.string().min(1).max(200),
    description: z.string().min(1).max(5000),
    /**
     * For sociale_kaart_update, optional reference to the provider being amended.
     * The approval flow uses this to jump into the edit screen with a diff.
     */
    relatedProviderId: DocId.optional(),
    /**
     * For kennisbank_aanvulling originating from a refuse-to-answer event,
     * or for antwoord_feedback, the user's question. Powers the
     * content-improvement loop.
     */
    originatingQuery: z.string().max(2000).optional(),
    /**
     * antwoord_feedback only — the structured "why" behind the thumbs-down
     * and the chat/message it points at, so an admin can trace the failing
     * answer back to its sources. chatId/messageId are the user's own
     * subcollection path (tenants/{t}/users/{u}/chats/...); the admin uses
     * them for provenance, not direct navigation.
     */
    /**
     * antwoord_feedback only — sterbeoordeling 1-5 van het antwoord van Mila.
     * Vervangt de duim omhoog/omlaag: geeft gradatie in plaats van een binaire
     * stem, met vrije tekst in `description` als het "waarom".
     */
    answerRating: z.number().int().min(1).max(5).optional(),
    answerFeedbackLabel: AnswerFeedbackLabel.optional(),
    chatId: DocId.optional(),
    messageId: DocId.optional(),
    /**
     * antwoord_feedback only — een embedded snapshot van de falende
     * antwoordtekst van Mila. chatId/messageId blijven als provenance, maar de
     * chat wordt na 30 dagen fysiek gepurged (ADR 0008); zonder deze snapshot
     * zou de eval-dataset de purge niet overleven. Losgekoppeld van de chat en
     * dus dangling-bestendig.
     */
    answerText: z.string().max(8000).optional(),
    /**
     * antwoord_feedback only — snapshot van de berichten vóór de falende
     * beurt (oudste eerst), zodat feedback op een vervolgvraag te beoordelen
     * is mét de voorafgaande context. Zelfde purge-argument als answerText:
     * de chat verdwijnt na 30 dagen (ADR 0008), dit snapshot niet. Cap 10
     * spiegelt de priorMessages-cap van het agent-request.
     */
    priorMessages: z
        .array(z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().max(8000),
    }))
        .max(10)
        .optional(),
    citedSources: z
        .array(z.object({
        chunkId: z.string().max(200),
        filename: z.string().max(500).optional(),
        title: z.string().max(500).optional(),
    }))
        .max(20)
        .optional(),
    /**
     * Cloud Storage paths of uploaded attachments.
     */
    attachmentUris: z.array(z.string().url()).default([]),
    status: ChangeRequestStatus.default('new'),
    /**
     * Populated on status transitions.
     */
    reviewedByUserId: DocId.optional(),
    reviewedAt: IsoTimestamp.optional(),
    reviewerNote: z.string().max(2000).optional(),
    /**
     * Gezet door de retentie-janitor zodra `originatingQuery` en `priorMessages`
     * zijn geleegd (#269). Dient als markering zodat een document niet elke run
     * opnieuw wordt bewerkt, en als bewijs richting de audit dat de kortere klok
     * op de consultinhoud daadwerkelijk heeft gelopen.
     */
    contentPurgedAt: IsoTimestamp.optional(),
});
/**
 * Submission payload. Tenant + user injected by the route.
 */
export const SubmitChangeRequestInput = z.object({
    type: ChangeRequestType,
    title: z.string().min(1).max(200),
    description: z.string().min(1).max(5000),
    relatedProviderId: DocId.optional(),
    originatingQuery: z.string().max(2000).optional(),
    /**
     * antwoord_feedback only — sterbeoordeling 1-5 van het antwoord van Mila.
     * Vervangt de duim omhoog/omlaag: geeft gradatie in plaats van een binaire
     * stem, met vrije tekst in `description` als het "waarom".
     */
    answerRating: z.number().int().min(1).max(5).optional(),
    answerFeedbackLabel: AnswerFeedbackLabel.optional(),
    chatId: DocId.optional(),
    messageId: DocId.optional(),
    answerText: z.string().max(8000).optional(),
    priorMessages: z
        .array(z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().max(8000),
    }))
        .max(10)
        .optional(),
    citedSources: z
        .array(z.object({
        chunkId: z.string().max(200),
        filename: z.string().max(500).optional(),
        title: z.string().max(500).optional(),
    }))
        .max(20)
        .optional(),
    attachmentUris: z.array(z.string().url()).max(5).optional(),
});
/**
 * Eén rij in het beoordelaarsoverzicht (issue #237): wie praat er met Mila en
 * beoordeelt niet? Bewust alleen aantallen en een datum. Linda spreekt mensen
 * persoonlijk aan en hoeft hier niet te lezen wát iemand vroeg; daarvoor is de
 * wachtrij op dezelfde pagina.
 */
export const MilaBeoordelaarRij = z.object({
    userId: DocId,
    displayName: z.string(),
    gesprekken: z.number().int().min(0),
    beoordelingen: z.number().int().min(0),
    laatsteBeoordelingAt: IsoTimestamp.optional(),
});
/**
 * Admin review action payload. Status transitions are validated at service layer.
 */
export const ReviewChangeRequestInput = z.object({
    action: z.enum(['approve', 'reject', 'request_info']),
    reviewerNote: z.string().max(2000).optional(),
});
//# sourceMappingURL=change-request.js.map